import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { api } from "@/lib/api";
import { isNativeApp } from "@/lib/utils";
import ProductCard from "@/components/ProductCard";
import PullToRefresh from "@/components/PullToRefresh";
import { ArrowRight, Zap, ShieldCheck, Recycle, TrendingUp, Search } from "lucide-react";

const heroImage = "https://images.unsplash.com/photo-1575404078738-d2f15b89d320?crop=entropy&cs=srgb&fm=jpg&w=1600&q=85";
const communityImage = "https://images.unsplash.com/photo-1567401893414-76b7b1e5a7a5?crop=entropy&cs=srgb&fm=jpg&w=1200&q=85";
const flatLayImage = "https://images.unsplash.com/photo-1576188973526-0e5d7047b0cf?crop=entropy&cs=srgb&fm=jpg&w=1200&q=85";

const categories = [
  { key: "vetements", label: "Vêtements", emoji: "👕", color: "bg-[color:var(--color-mint)]", count: "12k+" },
  { key: "chaussures", label: "Chaussures", emoji: "👟", color: "bg-[color:var(--color-coral)] text-white", count: "4.2k+" },
  { key: "sacs", label: "Sacs", emoji: "👜", color: "bg-[color:var(--color-yellow)]", count: "2.8k+" },
  { key: "accessoires", label: "Accessoires", emoji: "🕶️", color: "bg-black text-white", count: "5.1k+" },
];

// Accueil orienté produits pour l'app installée (iOS / Android)
function AppHome({ latest, onRefresh }) {
  return (
    <PullToRefresh onRefresh={onRefresh}>
    <div className="pb-2 fade-in" data-testid="app-home">
      <section className="px-4 pt-4">
        <Link to="/catalogue" className="flex items-center gap-2 border-2 border-black px-3 py-3 bg-[color:var(--color-surface)]" data-testid="app-home-search">
          <Search size={16} strokeWidth={2.5} />
          <span className="text-sm font-medium text-[color:var(--color-text-muted)]">Rechercher une marque, un style…</span>
        </Link>
      </section>
      <section className="mt-4 pl-4 flex gap-2 overflow-x-auto no-scrollbar pr-4">
        {categories.map(c => (
          <Link key={c.key} to={`/catalogue?category=${c.key}`} className={`${c.color} border-2 border-black px-3 py-2 whitespace-nowrap text-xs font-black uppercase tracking-wide shrink-0`} data-testid={`app-cat-${c.key}`}>
            {c.emoji} {c.label}
          </Link>
        ))}
        <Link to="/brocante-live" className="border-2 border-black px-3 py-2 whitespace-nowrap text-xs font-black uppercase tracking-wide text-[color:var(--color-coral)] shrink-0" data-testid="app-cat-live">● Brocante Live</Link>
      </section>
      <section className="mt-6 px-4">
        <div className="flex items-center justify-between mb-3">
          <h2 className="text-lg font-black tracking-tight">Nouveautés</h2>
          <Link to="/catalogue" className="label font-bold flex items-center gap-1" data-testid="app-home-see-all">Tout voir <ArrowRight size={12} strokeWidth={3} /></Link>
        </div>
        <div className="grid grid-cols-2 gap-3" data-testid="app-home-feed">
          {latest.map(p => <ProductCard key={p.product_id} product={p} />)}
        </div>
      </section>
    </div>
    </PullToRefresh>
  );
}

export default function Landing() {
  const [latest, setLatest] = useState([]);

  const loadLatest = () => api.get("/products?limit=12").then(r => setLatest(r.data)).catch(()=>{});

  useEffect(() => { loadLatest(); }, []);

  if (isNativeApp()) return <AppHome latest={latest} onRefresh={loadLatest} />;

  return (
    <div className="fade-in" data-testid="landing-page">
      {/* HERO */}
      <section className="relative bg-black text-white overflow-hidden">
        <div className="max-w-[1400px] mx-auto px-4 lg:px-8 pt-10 pb-12 lg:pt-16 lg:pb-20 grid grid-cols-1 lg:grid-cols-12 gap-8 items-center">
          <div className="lg:col-span-7 z-10">
            <div className="inline-flex items-center gap-2 bg-[color:var(--color-mint)] text-black px-3 py-1.5 mb-6 label font-bold">
              <Zap size={14} fill="currentColor" /> 482 articles ajoutés aujourd'hui
            </div>
            <h1 className="text-[44px] leading-[0.95] sm:text-6xl lg:text-[88px] font-black tracking-tighter mb-6" data-testid="hero-headline">
              VENDS.<br />
              <span className="text-[color:var(--color-mint)]">ACHÈTE.</span><br />
              PROFITE.
            </h1>
            <p className="text-base sm:text-lg max-w-md text-white/80 mb-8 leading-relaxed">
              La place française du marché de la mode seconde main. Pas de filtre, pas de blabla — juste tes articles préférés à prix juste.
            </p>
            <div className="flex flex-wrap gap-3">
              <Link to="/catalogue" className="btn btn-mint" data-testid="hero-cta-shop">
                Explorer <ArrowRight size={16} strokeWidth={3} />
              </Link>
              <Link to="/vendre" className="btn bg-transparent text-white border-white hover:bg-white hover:text-black" data-testid="hero-cta-sell">
                + Vendre un article
              </Link>
            </div>

            <div className="mt-6 flex flex-wrap items-center gap-3" data-testid="store-badges">
              <a href="https://apps.apple.com/fr/app/fripri/id6803243503" target="_blank" rel="noopener noreferrer" data-testid="badge-app-store">
                <img src="/app-store-badge.svg" alt="Télécharger dans l'App Store" className="h-12 w-auto" />
              </a>
              <a href="https://play.google.com/store/apps/details?id=com.fripri.app" target="_blank" rel="noopener noreferrer" data-testid="badge-play-store">
                <img src="/google-play-badge.png" alt="Disponible sur Google Play" className="h-[70px] w-auto -my-2" />
              </a>
            </div>

            <div className="mt-10 grid grid-cols-3 gap-6 lg:gap-10 max-w-md">
              <div>
                <div className="display-num text-2xl lg:text-3xl text-[color:var(--color-mint)]">32k</div>
                <div className="text-[10px] uppercase tracking-wider text-white/60 mt-1">articles actifs</div>
              </div>
              <div>
                <div className="display-num text-2xl lg:text-3xl text-[color:var(--color-mint)]">0%</div>
                <div className="text-[10px] uppercase tracking-wider text-white/60 mt-1">frais vendeurs</div>
              </div>
              <div>
                <div className="display-num text-2xl lg:text-3xl text-[color:var(--color-mint)]">FR</div>
                <div className="text-[10px] uppercase tracking-wider text-white/60 mt-1">made in france</div>
              </div>
            </div>
          </div>

          <div className="lg:col-span-5 relative">
            <div className="aspect-[4/5] overflow-hidden">
              <img src={heroImage} alt="Fripri community" className="w-full h-full object-cover" />
            </div>
            <div className="absolute -bottom-3 -left-3 lg:-left-6 bg-[color:var(--color-coral)] text-white p-3 lg:p-4 max-w-[200px] border-2 border-white">
              <p className="label text-white/80 mb-1">Tendance</p>
              <p className="font-bold leading-tight text-sm lg:text-base">Le vintage 90s explose. -60% vs neuf.</p>
            </div>
          </div>
        </div>

        {/* Marquee */}
        <div className="bg-[color:var(--color-mint)] text-black overflow-hidden py-3 border-t-2 border-white/10">
          <div className="marquee gap-8 label font-bold">
            {Array.from({ length: 3 }).map((_, i) => (
              <React.Fragment key={i}>
                <span>★ Paiement sécurisé Stripe</span>
                <span>•</span>
                <span>★ Livraison neutre en carbone</span>
                <span>•</span>
                <span>★ Vendeurs vérifiés</span>
                <span>•</span>
                <span>★ Communauté française</span>
                <span>•</span>
                <span>★ -82% CO₂ vs neuf</span>
                <span>•</span>
              </React.Fragment>
            ))}
          </div>
        </div>
      </section>

      {/* Categories */}
      <section className="max-w-[1400px] mx-auto px-4 lg:px-8 py-16 lg:py-24">
        <div className="flex items-end justify-between mb-8">
          <h2 className="text-3xl lg:text-5xl font-black tracking-tighter">Tu cherches quoi ?</h2>
          <Link to="/catalogue" className="hidden md:flex items-center gap-2 label font-bold hover:text-[color:var(--color-coral)]">Tout voir <ArrowRight size={14} /></Link>
        </div>
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-3 lg:gap-4">
          {categories.map((c) => (
            <Link key={c.key} to={`/catalogue?category=${c.key}`} className={`${c.color} border-2 border-black p-5 lg:p-6 card-hover relative aspect-[4/5] lg:aspect-square flex flex-col justify-between`} data-testid={`category-${c.key}`}>
              <div className="text-5xl lg:text-7xl">{c.emoji}</div>
              <div>
                <p className="label opacity-70 mb-1">{c.count} articles</p>
                <h3 className="text-2xl lg:text-3xl font-black tracking-tighter">{c.label}</h3>
              </div>
              <ArrowRight size={20} strokeWidth={3} className="absolute top-4 right-4" />
            </Link>
          ))}
        </div>
      </section>

      {/* Trust strip */}
      <section className="bg-[color:var(--color-surface)] py-12 lg:py-16">
        <div className="max-w-[1400px] mx-auto px-4 lg:px-8 grid grid-cols-1 md:grid-cols-3 gap-6">
          <div className="card border-2 border-black bg-white p-6 card-hover">
            <ShieldCheck size={32} strokeWidth={2} className="mb-3" />
            <h3 className="text-xl font-black mb-2 tracking-tight">Paiement sécurisé</h3>
            <p className="text-sm text-[color:var(--color-text-muted)]">Stripe protège chaque transaction. Tu ne payes rien si l'article ne te plaît pas.</p>
          </div>
          <div className="card border-2 border-black bg-[color:var(--color-mint)] p-6 card-hover">
            <Recycle size={32} strokeWidth={2} className="mb-3" />
            <h3 className="text-xl font-black mb-2 tracking-tight">Bon pour la planète</h3>
            <p className="text-sm">Un article de seconde main = 25kg de CO₂ évités. La planète te dit merci.</p>
          </div>
          <div className="card border-2 border-black bg-white p-6 card-hover">
            <TrendingUp size={32} strokeWidth={2} className="mb-3" />
            <h3 className="text-xl font-black mb-2 tracking-tight">0% de commission</h3>
            <p className="text-sm text-[color:var(--color-text-muted)]">Tu vends, tu encaisses. Tout. Vraiment.</p>
          </div>
        </div>
      </section>

      {/* Latest products feed */}
      <section className="max-w-[1400px] mx-auto px-4 lg:px-8 py-16 lg:py-24">
        <div className="flex items-end justify-between mb-8">
          <div>
            <div className="inline-flex items-center gap-2 bg-[color:var(--color-coral)] text-white px-2 py-1 label mb-3">
              <Zap size={12} fill="currentColor" /> Hot
            </div>
            <h2 className="text-3xl lg:text-5xl font-black tracking-tighter">Nouveaux drops</h2>
          </div>
          <Link to="/catalogue" className="hidden md:flex items-center gap-2 label font-bold hover:text-[color:var(--color-coral)]">Voir tout <ArrowRight size={14} /></Link>
        </div>
        {latest.length === 0 ? (
          <div className="text-center py-16 border-2 border-dashed border-black">
            <p className="label mb-3">Pas encore d'articles</p>
            <p className="text-2xl font-black mb-6">Sois le premier à drop un article ⚡</p>
            <Link to="/vendre" className="btn btn-mint">+ Mettre en vente</Link>
          </div>
        ) : (
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 lg:gap-6">
            {latest.slice(0, 8).map(p => <ProductCard key={p.product_id} product={p} />)}
          </div>
        )}
      </section>

      {/* Community / Mission */}
      <section className="bg-black text-white py-16 lg:py-24">
        <div className="max-w-[1400px] mx-auto px-4 lg:px-8 grid grid-cols-1 lg:grid-cols-12 gap-10 items-center">
          <div className="lg:col-span-5 order-2 lg:order-1">
            <div className="aspect-[4/5] overflow-hidden border-2 border-[color:var(--color-mint)]">
              <img src={communityImage} alt="Community" className="w-full h-full object-cover grayscale" />
            </div>
          </div>
          <div className="lg:col-span-7 order-1 lg:order-2">
            <div className="label text-[color:var(--color-mint)] mb-4">№ 001 — manifeste</div>
            <h2 className="text-4xl lg:text-6xl font-black tracking-tighter mb-6 leading-[1]">
              On ne <span className="text-[color:var(--color-mint)]">produit pas</span> plus.<br/>
              On fait <span className="line-through opacity-50">tourner</span><br/> <span className="text-[color:var(--color-coral)]">durer.</span>
            </h2>
            <p className="text-base lg:text-lg text-white/80 max-w-xl leading-relaxed mb-8">
              Fripri, c'est la communauté qui croit qu'un article bien fait mérite plus qu'une saison. Que la mode peut être bien fringuée sans cramer la planète. Et que vendre tes vêtements doit être aussi simple que prendre un selfie.
            </p>
            <Link to="/manifeste" className="btn bg-[color:var(--color-mint)] text-black hover:bg-white hover:text-black border-[color:var(--color-mint)] hover:border-white" data-testid="manifesto-link">
              Lire le manifeste <ArrowRight size={16} strokeWidth={3} />
            </Link>
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="max-w-[1400px] mx-auto px-4 lg:px-8 py-16 lg:py-24 text-center">
        <h2 className="text-4xl lg:text-7xl font-black tracking-tighter mb-6">
          Tes articles <span className="text-[color:var(--color-coral)]">méritent</span><br/> mieux que ton placard.
        </h2>
        <p className="text-base lg:text-lg text-[color:var(--color-text-muted)] max-w-xl mx-auto mb-10">
          Photographie. Décris. Publie. Sois payé. C'est tout. Vraiment.
        </p>
        <Link to="/vendre" className="btn btn-primary text-base px-8 py-5" data-testid="cta-bottom-sell">
          + Mettre en vente
        </Link>
      </section>
    </div>
  );
}
